import { useCallback, useEffect, useRef, useState } from 'react';

/** How often to re-probe our own origin while `navigator.onLine` claims we are offline. */
const PROBE_INTERVAL_MS = 5000;
/** Give up on a single probe after this long — a hung request is a failed probe. */
const PROBE_TIMEOUT_MS = 3000;
/** Side-effect-free 204 endpoint. Never probe /api/auth/session: it rotates the refresh token. */
const PROBE_URL = '/api/health';

const readNavigatorOnline = () =>
  typeof window === 'undefined' ? true : window.navigator.onLine;

/**
 * Did a real request reach our own origin? The only trustworthy way to tell a dead
 * network apart from a server that genuinely answered — `navigator.onLine` can't, and
 * neither can NextAuth's `getSession()`, which resolves to `null` for both.
 */
export const probeServerReachable = async (): Promise<boolean> => {
  const controller = new AbortController();
  // Manual timeout rather than AbortSignal.timeout — no polyfill assumptions.
  const timeout = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
  try {
    await fetch(PROBE_URL, { cache: 'no-store', signal: controller.signal });
    return true;
  } catch {
    return false;
  } finally {
    clearTimeout(timeout);
  }
};

/**
 * Whether we believe the user actually has a working connection.
 *
 * `navigator.onLine` lies often — most importantly it reports offline while a laptop
 * switches between a mobile hotspot and wifi, even though the network stack recovers
 * immediately. Acting on that alone makes the app announce an outage (and, elsewhere,
 * previously made it treat the user as signed out) during a blip that never happened.
 *
 * So we only report offline when BOTH signals agree: the navigator says offline AND a
 * real request to our own origin fails.
 */
export const useIsOnline = () => {
  const [navigatorOnline, setNavigatorOnline] = useState<boolean>(true);
  const [isProbeReachable, setIsProbeReachable] = useState<boolean>(true);

  const probe = useCallback(async () => {
    setIsProbeReachable(await probeServerReachable());
  }, []);

  useEffect(() => {
    const syncFromNavigator = () => setNavigatorOnline(readNavigatorOnline());
    syncFromNavigator();
    window.addEventListener('online', syncFromNavigator);
    window.addEventListener('offline', syncFromNavigator);
    return () => {
      window.removeEventListener('online', syncFromNavigator);
      window.removeEventListener('offline', syncFromNavigator);
    };
  }, []);

  // Hold the latest probe in a ref so the polling effect depends only on
  // `navigatorOnline` — otherwise it would tear down and restart on every result.
  const probeRef = useRef(probe);
  probeRef.current = probe;

  useEffect(() => {
    if (navigatorOnline) {
      // The navigator is the cheaper signal; once it recovers we trust it and stop
      // spending requests. Reset so a future offline event starts from a clean slate.
      setIsProbeReachable(true);
      return;
    }
    probeRef.current();
    const interval = setInterval(() => probeRef.current(), PROBE_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [navigatorOnline]);

  return { isOnline: navigatorOnline || isProbeReachable };
};
